import type { SolSpeechAuthorization } from '../../data/sol/sol-contract';
import type { SolReadAloud as SolReadAloudController } from './use-sol-read-aloud';

/**
 * The Read Aloud control for ONE Sol answer. M9 / S09-04, Pass B.
 *
 * **IT IS RENDERED ONLY FOR AN ELIGIBLE GROUNDED ANSWER** — supported,
 * inference, conflicting and unknown (D-04-5). Counts, close matches,
 * clarifications, unsupported questions and failures have no control at all,
 * because there is no validated answer to read.
 *
 * **AND ONLY WHEN THE SERVER ISSUED AUTHORIZATION.** No ticket means Read Aloud
 * is switched off, unconfigured, or the content could not be prepared safely —
 * and in every one of those cases the honest thing is no button, not a button
 * that apologises when tapped.
 *
 * **THERE IS NO SYSTEM-VOICE FALLBACK, ANYWHERE** (D-04-4). When the natural
 * voice is unavailable, Read Aloud says so. It never quietly switches to the
 * browser's robotic synthesiser, which would be a different product wearing this
 * one's label.
 *
 * (The provider is deliberately not named even in a comment — D-012, and
 * `stt-boundary-guards.test.ts` asserts that absence across the whole of `src/`
 * so `check:secrets:build` can assert a clean built artifact.)
 *
 * **NOTHING AUTOPLAYS.** Every sound this app makes follows a tap.
 */

export interface SolReadAloudProps {
  /** The exchange id. One playback exists at a time, across the whole screen. */
  readonly answerId: number;
  readonly speech: SolSpeechAuthorization;
  readonly controller: SolReadAloudController;
  /**
   * **ONLY ONE AUDIO DIRECTION AT A TIME.** True while the microphone is live
   * or its audio is still becoming text. Disabled rather than hidden: a control
   * that vanishes mid-task reads as a bug, and it comes back in a moment.
   */
  readonly disabled?: boolean;
}

export function SolReadAloud({ answerId, speech, controller, disabled }: SolReadAloudProps) {
  const session = controller.session;
  const mine = session !== null && session.answerId === answerId ? session : null;
  const live = mine !== null && (mine.state === 'preparing' || mine.state === 'playing');
  const parts = speech.chunks.length;

  return (
    <p className="sol-read-aloud-row">
      <button
        type="button"
        className="sol-read-aloud"
        // **THE ACCESSIBLE NAME IS THE ACTION, NOT THE STATE.** A screen reader
        // announces what tapping will do.
        aria-pressed={live}
        disabled={disabled === true}
        onClick={() => {
          if (live) {
            controller.stop();
            return;
          }
          controller.start(answerId, speech);
        }}
      >
        {live ? 'Stop reading' : 'Read answer aloud'}
      </button>

      {/* **PREPARING IS ITSELF STOPPABLE.** The button above already says "Stop
          reading" while a chunk is being generated — that request costs money,
          and making him wait for audio he no longer wants would be the app's
          convenience rather than his. */}
      {mine?.state === 'preparing' ? <span className="sol-read-aloud-note">Preparing…</span> : null}

      {/* Honest about a long answer arriving in parts, rather than letting the
          pause between them read as a fault. */}
      {mine?.state === 'playing' && parts > 1 ? (
        <span className="sol-read-aloud-note">
          Part {mine.index + 1} of {parts}
        </span>
      ) : null}

      {mine?.state === 'failed' ? (
        <span className="sol-read-aloud-note sol-read-aloud-note--failure">
          Read Aloud isn&rsquo;t available right now.
        </span>
      ) : null}

      {/* **NEVER SILENTLY ENDED AS THOUGH IT FINISHED.** Half an answer that
          sounds complete is the one outcome worse than no audio. */}
      {mine?.state === 'incomplete' ? (
        <span className="sol-read-aloud-note sol-read-aloud-note--failure">
          Read Aloud stopped before the full answer could be read. The answer above is complete.
        </span>
      ) : null}

      {mine?.state === 'failed' || mine?.state === 'incomplete' ? (
        <button
          type="button"
          className="sol-read-aloud"
          disabled={disabled === true}
          // A deliberate second tap on the SAME answer and the SAME ticket. It
          // never re-asks Sol and never re-reasons.
          onClick={() => {
            controller.retry();
          }}
        >
          Try reading again
        </button>
      ) : null}
    </p>
  );
}
